import { ESCAPED_DOLLAR, MATH_FIELD } from 'constants/index';
import DOMPurify from 'dompurify';
import katex from 'katex';

/**
 * Render KaTeX + markdown-like formatting.
 * Math expressions are displayed on new lines.
 */
export const renderMarkupWithMath = ({ text }) => {
  if (!text) return '';

  text = text.replace(/\r\n|\r/g, '\n');

  let html = text;

  html = html.replace(/\\\$/g, ESCAPED_DOLLAR);

  // Convert double escaped backslashes from BE ("\\sqrt") → "\sqrt"
  html = html.replace(/\\\\/g, '\\');

  // Fix wrongly escaped slashes
  html = html.replace(/\\\//g, '/');

  // Replace any actual newline characters with <br>
  html = html.replace(/\n/g, '<br>');

  // --- MATH FIRST ---

  // Block math: $$...$$ → full-width centered block
  html = html.replace(
    /\$\$([\s\S]+?)\$\$/g,
    (_, tex) =>
      `<div class="math-block">${katex.renderToString(tex.trim(), {
        throwOnError: false,
        displayMode: true
      })}</div>`
  );

  // Inline math: $...$ → render and add line breaks around
  html = html.replace(
    /\$([^$]+)\$/g,
    (_, tex) =>
      `<div class="math-inline">${katex.renderToString(tex.trim(), {
        throwOnError: false,
        displayMode: false
      })}</div>`
  );

  html = html.replace(new RegExp(ESCAPED_DOLLAR, 'g'), '$');

  html = DOMPurify.sanitize(html);

  // --- MARKDOWN HEADERS ---
  html = html
    .replace(/^### (.*)$/gim, '<h3>$1</h3>')
    .replace(/^## (.*)$/gim, '<h2>$1</h2>')
    .replace(/^# (.*)$/gim, '<h1>$1</h1>');

  // --- LISTS ---
  html = html
    .replace(/^[*-]\s+(.*)$/gim, '<ul><li>$1</li></ul>')
    .replace(/^\d+\.\s+(.*)$/gim, '<ol><li>$1</li></ol>')
    .replace(/<\/ul>\s*<ul>/g, '')
    .replace(/<\/ol>\s*<ol>/g, '');

  // --- INLINE STYLES ---
  html = html
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<u>$1</u>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/(?<!\S)\*(?!\s)(.+?)(?<!\s)\*(?!\S)/g, '<em>$1</em>');

  // --- REMOVE DOTS DIRECTLY AFTER MATH EXPRESSIONS (+ trim nearby spaces) ---
  html = html.replace(/(<\/span>|<\/div>)\s*\.\s*/g, '$1 ');

  // Remove commas directly after math expressions (+ trim nearby spaces)
  html = html.replace(/(<\/span>|<\/div>)\s*,\s*/g, '$1 ');

  // --- NEWLINES ---
  html = html.replace(/\\n/g, '<br>'); // handle "\n" literals

  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  return html.trim();
};

// Helper function for EditorField
export const traverseNodes = ({ node, parentTag = '' }) => {
  if (!node) return '';

  // Handle text nodes
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }

  // Handle <br> explicitly (important!)
  if (
    node.nodeType === Node.ELEMENT_NODE &&
    node.tagName.toLowerCase() === 'br'
  ) {
    return '\\n'; // store as escaped newline
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const tag = node.tagName.toLowerCase();

    if (tag === 'br') return '\\n';
    if (tag === MATH_FIELD) return `$${node.value || ''}$`;

    const inner = Array.from(node.childNodes)
      .map((child) => traverseNodes({ node: child }))
      .join('');

    const hasNext = node.nextSibling !== null;

    switch (tag) {
      case 'p':
      case 'div':
      case 'li':
        /**
         * Only add a newline if there is another element after this one.
         * This prevents the "Trailing Double Newline" in the database.
         */
        return inner.trim() || hasNext ? `${inner}${hasNext ? '\\n' : ''}` : '';

      case 'strong':
      case 'b':
        return `**${inner}**`;
      case 'em':
      case 'i':
        return `*${inner}*`;
      case 'u':
        return `_${inner}_`;
      default:
        return inner;
    }
  }
  return '';
};
